import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, input, output, signal } from '@angular/core';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { AppointmentDb } from 'lib/cosmos/appointment/appointmentDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { DateTime } from 'luxon';
import { calendarHelper } from './calendar.helper.js';

@Component({
	selector: 'mod-appointment-form',
	templateUrl: './appointment-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AppointmentFormComponent
	extends TypeCoreComponent<AppointmentDb.IAppointmentItem | undefined>
	implements OnInit
{
	public readonly isInDialog = input<boolean>(false);
	public readonly create = output<AppointmentDb.IAppointmentItem>();

	public readonly appointmentLength = calendarHelper.appointmentLength;
	private readonly clients = signal<UserDb.IUserItem[] | undefined>(undefined);

	public clientSelectionItems = computed<SelectItemField<UserDb.IUserItem>[]>(() => {
		const clients = this.clients();

		if (!clients) {
			return [];
		}

		return clients.map((m) => {
			const item: SelectItemField<UserDb.IUserItem> = {
				data: m,
				title: nameHelper.getUserName(m.email, m.firstName, m.lastName),
				value: m.id,
				avatar: m.avatar
			};

			return item;
		});
	});

	public readonly form = {
		id: signal<string | undefined>(undefined),
		name: signal<string | undefined>(undefined),
		notes: signal<string | undefined>(undefined),
		clientId: signal<string | undefined>(undefined),
		trainerId: signal<string | undefined>(undefined),
		length: signal<number | undefined>(undefined),
		dateTime: signal<DateTime | undefined>(undefined),
		location: signal<string | undefined>(undefined),
		meetingUrl: signal<string | undefined>(undefined)
	};

	public readonly isEditForm: Signal<boolean> = computed(() => {
		if (this.form.id()) {
			return true;
		}
		return false;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();
		const form = this.form;

		const name = form.name();
		const clientId = form.clientId();
		const trainerId = form.trainerId();
		const dateTime = form.dateTime();
		const length = form.length();

		if (!name || !clientId || !dateTime || !length || !trainerId) {
			return undefined;
		}

		return this.dependencies.httpService
			.upsert<AppointmentDb.IAppointmentItem, AppointmentDb.IBaseAppointmentItem>({
				type: 'appointment',
				existingItem: sharedData?.data,
				createModel: {
					name: name,
					clientId: clientId,
					trainerId: trainerId,
					dateTime: this.dependencies.timeService.toCosmosDbDate(dateTime),
					length: length,
					state: 'active',
					meetingUrl: form.meetingUrl(),
					location: form.location(),
					notes: form.notes()
				},
				patchModel: (item) => {
					return {
						id: item.id,
						name: name,
						dateTime: this.dependencies.timeService.toCosmosDbDate(dateTime),
						length: length,
						location: form.location(),
						meetingUrl: form.meetingUrl(),
						notes: form.notes()
					};
				}
			})
			.pipe(
				map((response) => {
					super.notifyUpsert('Appointment', this.isEditForm());
					this.create.emit(response.data);

					super.updateSharedData({
						data: response.data,
						linkedItems: response.linkedItems
					});

					if (!this.isInDialog() && !this.isEditForm()) {
						this.dependencies.navigateService
							.actionRoute((routes) => routes.calendarRoutes.view.children.upcoming)
							.navigate();
					}
				})
			);
	});

	constructor() {
		super();

		// set trainer id if it was not set
		if (!this.form.trainerId()) {
			const authUser = this.authUser();

			if (authUser?.userType === 'trainer') {
				// user current user as trainer id
				this.form.trainerId.set(authUser.id);
			}
		}

		this.dataInit = () => {
			this.initClients();
		};

		this.sharedDataInit = (sharedData) => {
			if (sharedData?.data) {
				this.form.id.set(sharedData.data.id);
				this.form.name.set(sharedData.data.system.name);
				this.form.notes.set(sharedData.data.notes);
				this.form.clientId.set(sharedData.data.clientId);
				this.form.trainerId.set(sharedData.data.trainerId);
				this.form.length.set(sharedData.data.length);
				this.form.meetingUrl.set(sharedData.data.meetingUrl);
				this.form.location.set(sharedData.data.location);
				this.form.dateTime.set(this.timeService.getLuxonDateTime(sharedData.data.dateTime));
			}
		};
	}

	private initClients(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<UserDb.IUserItem[]>((builder) => builder.withCustomApiRoute('user', 'listClients').build())
				.pipe(
					map((response) => {
						this.clients.set(response.data);
					})
				)
		);
	}
}
